import { useEffect, useMemo, useRef, useState } from "react"

import { CalendarDays, ChevronLeft, ChevronRight, Loader2, SlidersHorizontal } from "lucide-react"

import { Button } from "@/components/ui/button"
import { ApiError, getMeetings } from "@/lib/api"
import { useI18n, type MessageKey } from "@/i18n"
import { monthGrid, parseIsoDate, toIsoDate } from "@/lib/calendar"
import { cn } from "@/lib/utils"
import type { CalendarMeeting, MeetingContext } from "@/types/api"

const WEEKDAYS: MessageKey[] = [
  "meetings.weekday.mon",
  "meetings.weekday.tue",
  "meetings.weekday.wed",
  "meetings.weekday.thu",
  "meetings.weekday.fri",
  "meetings.weekday.sat",
  "meetings.weekday.sun",
]

interface MeetingCalendarProps {
  /** Receives the picked meeting and the chat message that represents the pick. */
  onPick: (meeting: MeetingContext, text: string) => void
  disabled?: boolean
}

type Load =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; meetings: CalendarMeeting[] }

function formatTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
}

function formatDay(iso: string, locale: string): string {
  return parseIsoDate(iso).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
}

/** Calendar icon in Work IQ mode: pick a day, see that day's meetings, prepare for or recap one. */
export function MeetingCalendar({ onPick, disabled = false }: MeetingCalendarProps) {
  const { t, locale } = useI18n()
  const today = new Date()
  const todayIso = toIsoDate(today)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() })
  const [day, setDay] = useState(todayIso)
  const [load, setLoad] = useState<Load>({ status: "loading" })
  const [custom, setCustom] = useState<CalendarMeeting | null>(null)
  const [instructions, setInstructions] = useState("")
  const [customKind, setCustomKind] = useState<"prep" | "recap">("prep")
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoad({ status: "loading" })
    void (async () => {
      try {
        const meetings = await getMeetings(day, Intl.DateTimeFormat().resolvedOptions().timeZone)
        if (!cancelled) setLoad({ status: "ready", meetings })
      } catch (error) {
        if (cancelled) return
        const message = error instanceof ApiError ? error.message : t("meetings.loadFailed")
        setLoad({ status: "error", message })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, day, t])

  const cells = useMemo(() => monthGrid(view.year, view.month), [view.year, view.month])
  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(locale, { month: "long", year: "numeric" })
  const shiftMonth = (delta: number) =>
    setView((v) => {
      const next = new Date(v.year, v.month + delta, 1)
      return { year: next.getFullYear(), month: next.getMonth() }
    })

  const defaultKind = (meeting: CalendarMeeting): "prep" | "recap" => (new Date(meeting.end) <= new Date() ? "recap" : "prep")

  const openCustom = (meeting: CalendarMeeting) => {
    setCustomKind(defaultKind(meeting))
    setInstructions("")
    setCustom(meeting)
  }

  const pick = (meeting: CalendarMeeting, userInstructions = "", kindOverride?: "prep" | "recap") => {
    const kind = kindOverride ?? defaultKind(meeting)
    const when = `${formatDay(day, locale)}, ${formatTime(meeting.start, locale)}`
    const label = userInstructions
      ? t("meetings.messageCustom")
      : kind === "recap"
        ? t("meetings.messageRecap")
        : t("meetings.messagePrep")
    const text = `${label}: ${meeting.subject} (${when})${userInstructions ? `
${userInstructions}` : ""}`
    setOpen(false)
    setCustom(null)
    setInstructions("")
    onPick(
      {
        kind,
        subject: meeting.subject,
        start: meeting.start,
        end: meeting.end,
        organizer: meeting.organizer,
        attendees: meeting.attendees,
        ...(userInstructions ? { instructions: userInstructions } : {}),
      },
      text,
    )
  }

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="rounded-full text-fg3 hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label={t("meetings.toggleLabel")}
        aria-expanded={open}
        title={t("meetings.toggleTitle")}
        data-testid="meeting-calendar-toggle"
      >
        <CalendarDays className="size-4" />
      </Button>

      {open && (
        <div
          className="absolute bottom-full left-0 z-20 mb-2 w-80 rounded-lg border bg-card p-3 text-sm shadow-card"
          data-testid="meeting-calendar"
        >
          <div className="mb-2 flex items-center justify-between">
            <Button variant="secondary" size="icon" aria-label={t("meetings.previousMonth")} onClick={() => shiftMonth(-1)}>
              <ChevronLeft className="size-4" />
            </Button>
            <span className="font-medium" aria-live="polite">
              {monthLabel}
            </span>
            <Button variant="secondary" size="icon" aria-label={t("meetings.nextMonth")} onClick={() => shiftMonth(1)}>
              <ChevronRight className="size-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground">
            {WEEKDAYS.map((d) => (
              <div key={d}>{t(d)}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {cells.map((date, index) => {
              if (!date) return <div key={`blank-${index}`} />
              const iso = toIsoDate(date)
              const selected = iso === day
              return (
                <button
                  key={iso}
                  type="button"
                  aria-pressed={selected}
                  aria-label={formatDay(iso, locale)}
                  onClick={() => setDay(iso)}
                  className={cn(
                    "h-8 rounded-md text-xs hover:bg-accent",
                    selected && "bg-primary text-primary-foreground hover:bg-primary",
                    iso === todayIso && !selected && "font-semibold underline",
                  )}
                >
                  {date.getDate()}
                </button>
              )
            })}
          </div>

          <div className="mt-3 border-t pt-2" data-testid="meeting-calendar-list">
            <div className="mb-1 text-xs text-muted-foreground">{formatDay(day, locale)}</div>
            {load.status === "loading" && (
              <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" /> {t("meetings.loading")}
              </div>
            )}
            {load.status === "error" && <p className="py-2 text-xs text-destructive">{load.message}</p>}
            {load.status === "ready" && load.meetings.length === 0 && (
              <p className="py-2 text-xs text-muted-foreground">{t("meetings.none")}</p>
            )}
            {load.status === "ready" && load.meetings.length > 0 && (
              <ul className="max-h-56 space-y-1 overflow-y-auto">
                {load.meetings.map((meeting) => {
                  const past = new Date(meeting.end) <= new Date()
                  return (
                    <li key={meeting.id} className="flex items-start gap-1">
                      <button
                        type="button"
                        onClick={() => pick(meeting)}
                        disabled={disabled}
                        className="flex min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent disabled:opacity-50"
                      >
                        <span className="w-24 shrink-0 text-xs text-muted-foreground">
                          {meeting.is_all_day
                            ? t("meetings.allDay")
                            : `${formatTime(meeting.start, locale)} – ${formatTime(meeting.end, locale)}`}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{meeting.subject}</span>
                          <span className="text-xs text-muted-foreground">{past ? t("meetings.followUp") : t("meetings.prepare")}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openCustom(meeting)}
                        disabled={disabled}
                        aria-label={t("meetings.optionsFor", { subject: meeting.subject })}
                        title={t("meetings.optionsTitle")}
                        className="mt-1.5 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                      >
                        <SlidersHorizontal className="size-3.5" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {custom && (
            <div className="mt-2 border-t pt-2" data-testid="meeting-custom">
              <div className="mb-1 truncate text-xs text-muted-foreground">{t("meetings.optionsHeading", { subject: custom.subject })}</div>
              <div className="mb-2 flex gap-1" role="radiogroup" aria-label={t("meetings.defaultMode")}>
                {(["prep", "recap"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={customKind === k}
                    onClick={() => setCustomKind(k)}
                    className={cn(
                      "flex-1 rounded-md border px-2 py-1 text-xs",
                      customKind === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent",
                    )}
                  >
                    {k === "prep" ? t("meetings.prepare") : t("meetings.followUp")}
                  </button>
                ))}
              </div>
              <textarea
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
                rows={3}
                maxLength={2000}
                placeholder={t("meetings.instructionPlaceholder")}
                aria-label={t("meetings.instructionLabel")}
                className="w-full resize-none rounded-md border bg-background p-2 text-xs"
              />
              <div className="mt-1 flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setCustom(null)}>
                  {t("meetings.cancel")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={disabled}
                  onClick={() => pick(custom, instructions.trim(), customKind)}
                >
                  {t("meetings.send")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
